import axios from "axios";
import { SelectEffect, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import {
    scenariosReceived,
    getScenarios as getAllScenarios,
    scenarioSelected
} from "../actions/Scenarios";
import {
    GetScenariosAction,
    GET_SCENARIOS,
    PostNewScenarioAction,
    POST_NEW_SCENARIO,
    DELETE_SCENARIO,
    DeleteScenarioAction,
    ScenariosReceivedAction,
    ScenarioSelectedAction,
    UpdateScenarioAction,
    UPDATE_SCENARIO
} from "../actions/Scenarios/types";
import { showError } from "../components/commonUtilities";
import { Scenario } from "../components/curb/ManagerAnalyzer/Manager/SelectScenarios/helpers";
import { GeneratorYieldType } from "../interfaces/ActionReducer/GetMethod/GetResponse";
import { projectSelected } from "actions";
import { ToggleCreateOptionsPanel } from "actions/regulation/CreateRegulation/types";
import { ProjectSelectedAction } from "actions/Projects/types";
import { RootState } from "reducers";

function* getScenarios(
    action: GetScenariosAction
): Generator<
    | Promise<any>
    | SelectEffect
    | GeneratorYieldType<ScenariosReceivedAction["payload"] | ScenarioSelectedAction["payload"]>,
    void,
    | ScenariosReceivedAction["payload"]["data"]
    | ScenarioSelectedAction["payload"]["selectedScenario"]
> {
    try {
        let scenarios = (yield axios
            .get("/api/scenario/", { params: action.payload.params })
            .then((res) => res.data.results)
            .then((res) => {
                if (Array.isArray(res)) {
                    let result = res.map((scenario: Scenario) => {
                        scenario = {
                            ...scenario,
                            label: scenario.name,
                            value: scenario.id
                        };
                        const baseScenario = {
                            ...scenario,
                            listView: true,
                            default: false,
                            mapView: true
                        };

                        if (scenario?.project_id === 1) {
                            if (scenario?.name === "Existing") {
                                return {
                                    ...baseScenario,
                                    label: "Existing Conditions",
                                    listView: false,
                                    default: true
                                };
                            } else if (scenario?.name === "General") {
                                return {
                                    ...baseScenario,
                                    default: true
                                };
                            }
                        }

                        return baseScenario;
                    });
                    return result;
                }
            })) as ScenariosReceivedAction["payload"]["data"];

        yield put(scenariosReceived({ data: scenarios }));

        // Keep selected scenario in sync. If the fetched list contains an updated
        // version of the currently selected scenario, dispatch scenarioSelected with it.
        const currentSelectedScenario = (yield select(
            (state: RootState) => state.Scenarios.selectedScenario
        )) as ScenarioSelectedAction["payload"]["selectedScenario"];

        if (currentSelectedScenario && Array.isArray(scenarios)) {
            const updatedSelectedScenario = (scenarios as Scenario[]).find(
                (scenario) => scenario.id === currentSelectedScenario.id
            );

            if (updatedSelectedScenario) {
                yield put(
                    scenarioSelected({
                        selectedScenario: updatedSelectedScenario
                    })
                );
            }
        }
    } catch (e) {
        showError(e);
    }
}

export function* getScenariosSaga() {
    yield takeLatest(GET_SCENARIOS, getScenarios);
}

function* postNewScenario(
    action: PostNewScenarioAction
): Generator<
    | SelectEffect
    | Promise<Scenario>
    | GeneratorYieldType<ScenarioSelectedAction["payload"] | ProjectSelectedAction["payload"]>,
    void,
    ScenarioSelectedAction["payload"]["selectedScenario"]
> {
    try {
        const selectedProject = (state: RootState) => state.Projects.selectedProject;

        let json = yield axios.post("/api/scenario/", action.payload).then((res) => res.data);

        const currentSelectedProject = yield select(selectedProject);
        const newScenario = json;
        if (json) {
            json = {
                ...json,
                label: json.name,
                value: json.id,
                listView: true,
                default: false,
                mapView: true
            };
        }

        yield put(scenarioSelected({ selectedScenario: json }));
        const updatedProject = {
            ...currentSelectedProject,
            scenarios: [...(currentSelectedProject as any).scenarios, newScenario]
        };

        yield put(projectSelected({ data: updatedProject }));

        if (action.payload.afterProcess && json) {
            action.payload.afterProcess(json);
        }
    } catch (e) {
        showError(e);
    }
}

//get all the scenarios after succesful post request
export function* postNewScenarioSaga() {
    yield takeLatest(POST_NEW_SCENARIO, postNewScenario);
}

function* deleteScenario(action: DeleteScenarioAction) {
    try {
        yield Promise.all(
            action.payload?.scenarioId?.map((id, index) => {
                return axios.delete(`api/scenario/${id}/`).then((response) => {
                    return response.data;
                });
            })
        ).catch((e) => showError(e));
        if (action.payload.afterProcess) {
            action.payload.afterProcess();
        }
        yield put(getAllScenarios({ params: { fields: "id,name,project_id,created" } }));
    } catch (error) {
        showError(error);
    }
}

export function* deleteScenarioSaga() {
    yield takeLatest(DELETE_SCENARIO, deleteScenario);
}

function* updateScenario(action: UpdateScenarioAction) {
    try {
        yield axios.patch(`/api/scenario/${action.payload.scenario_id}/`, {
            name: action.payload.name
        });

        if (action.payload.afterProcess) {
            action.payload.afterProcess();
        }
    } catch (e) {
        showError(e);
    }
}

export function* UpdateScenarioSaga() {
    yield takeEvery(UPDATE_SCENARIO, updateScenario);
}
